import { createContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { setAccessToken, clearAccessToken } from "../utils/tokenStore";

const isTokenExpired = (token, bufferSeconds = 0) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now() + bufferSeconds * 1000;
  } catch {
    return true;
  }
};

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const backendUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";
  const [profile, setProfile] = useState(null);
  // Access token lives in memory only — never persisted to localStorage.
  // The httpOnly refresh-token cookie (set by the backend) restores the session on reload.
  const [accessToken, setAccessTokenState] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userFullName, setUserFullName] = useState('');
  const navigate = useNavigate();
  const pageTitle = 'Couplendar';
  const [emailVerified, setEmailVerified] = useState(false);

  const updateTokenState = (token) => {
    setAccessTokenState(token);
    setAccessToken(token); // sync to module-level store for axiosInstance
  };

  const getUserProfile = useCallback(async (token) => {
    try {
      const response = await fetch(backendUrl + "api/auth/profile/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const data = await response.json();
      if (response.status === 200) {
        setProfile(data);
        setUserFullName(data.fullname || '');
        setEmailVerified(!!data.email_verified);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [backendUrl]);

  // Restore session from the httpOnly refresh-token cookie
  const restoreSession = useCallback(async () => {
    try {
      const response = await fetch(backendUrl + "api/token/cookie-refresh/", {
        method: "POST",
        credentials: "include", // send the httpOnly cookie
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        updateTokenState(data.access);
        setUser(jwtDecode(data.access));
        await getUserProfile(data.access);
        return true;
      }
    } catch {
      // No valid cookie — user is logged out
    }
    return false;
  }, [backendUrl, getUserProfile]);

  useEffect(() => {
    restoreSession().finally(() => setLoading(false));
  }, [restoreSession]);

  const loginUser = async (email, password) => {
    const response = await fetch(backendUrl + "api/token/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // receive the refresh-token cookie
      body: JSON.stringify({ username: email, password }),
    });

    if (response.ok) {
      const data = await response.json();
      // Store access token in memory only; backend already set the refresh cookie
      updateTokenState(data.access);
      setUser(jwtDecode(data.access));
      await getUserProfile(data.access);
      navigate("/today");
    } else {
      let errorMessage = "Login failed";
      try {
        const data = await response.json();
        if (data.detail) errorMessage = data.detail;
        else if (data.error) errorMessage = data.error;
        else if (data.message) errorMessage = data.message;
        else if (data.username || data.password) {
          const errors = [];
          if (data.username) errors.push(Array.isArray(data.username) ? data.username[0] : data.username);
          if (data.password) errors.push(Array.isArray(data.password) ? data.password[0] : data.password);
          errorMessage = errors.join('\n');
        }
      } catch { /* ignore parse error */ }
      throw new Error(errorMessage);
    }
  };

  const logoutUser = async () => {
    try {
      // Ask the backend to clear the httpOnly refresh-token cookie
      await fetch(backendUrl + "api/token/logout/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
    } catch { /* ignore network errors on logout */ }

    updateTokenState(null);
    clearAccessToken();
    setUser(null);
    setProfile(null);
    setEmailVerified(false);
    setUserFullName('');
    navigate("/login");
  };

  const updateToken = async () => {
    const response = await fetch(backendUrl + "api/token/cookie-refresh/", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      updateTokenState(data.access);
      setUser(jwtDecode(data.access));
      await getUserProfile(data.access);
    } else {
      logoutUser();
    }
  };

  // Proactively refresh before the access token expires
  useEffect(() => {
    const REFRESH_INTERVAL = 1000 * 60 * 4; // 4 minutes
    const interval = setInterval(() => {
      if (accessToken) updateToken();
    }, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [accessToken]);

  // Validation check every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (accessToken && isTokenExpired(accessToken)) {
        logoutUser();
      }
    }, 1000 * 30);
    return () => clearInterval(interval);
  }, [accessToken]);

  const refreshProfile = async () => {
    if (accessToken) return await getUserProfile(accessToken);
    return false;
  };

  const isAuthenticated = () => {
    if (!accessToken || !user) return false;
    return !isTokenExpired(accessToken);
  };

  const validateAuthAndLogout = () => {
    if (accessToken && isTokenExpired(accessToken)) {
      logoutUser();
      return false;
    }
    return true;
  };

  // Keep authTokens shape compatible with existing consumers that read authTokens.access
  const authTokens = accessToken ? { access: accessToken } : null;

  const contextData = {
    user,
    authTokens,
    loginUser,
    logoutUser,
    backendUrl,
    profile,
    pageTitle,
    userFullName,
    emailVerified,
    loading,
    refreshProfile,
    isAuthenticated,
    validateAuthAndLogout,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {children}
    </AuthContext.Provider>
  );
};
